import React, { useMemo, useState } from 'react';
import { overlays, actions } from '@ecosystem/newron-design-system';
import { CustomMenuModalSettings, Period, Periodicity } from '../../types';

import { addDays, startOfWeekYear, FirstWeekContainsDate } from 'date-fns';

import { CustomWeekPicker } from './custom/weekPicker';
import { CustomMonthPicker } from './custom/monthPicker';
import { MAX_ALLOWED_COMPARISON_PERIODS, WARNING_MESSAGE_ON_APPLY } from './constants';
import { MenuSeparatorComponent } from './menu.separator';
import { MonthPickerParams, WeekPickerParams } from './types';
import { GenericError } from '../../design/error/genericError';

const { Modal } = overlays;
const { Button } = actions;

export interface CustomModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentSelectedPeriods?: string[];
  customMenuModalSettings: CustomMenuModalSettings;
  onApply: (start: Period[], end: Period[]) => void;
}

type PeriodWithDate = Period & { date: Date };

type ComparisonPeriod = {
  from: PeriodWithDate;
  to: PeriodWithDate;
};

export const CustomComparisonModal: React.FC<CustomModalProps> = ({
  isOpen,
  onClose,
  onApply,
  currentSelectedPeriods,
  customMenuModalSettings,
}: CustomModalProps) => {
  const [_, ...currentCustomPeriods] = currentSelectedPeriods ?? [];

  const { customMenuOptions } = customMenuModalSettings.customComparisonPeriods;

  const periodOptions = useMemo(() => {
    return customMenuOptions.map(periodOptions => ({
      ...periodOptions,
      date:
        periodOptions.periodicity === Periodicity.Weekly
          ? weekNumberToDate(periodOptions.name)
          : new Date(periodOptions.name),
    }));
  }, [customMenuModalSettings]);

  const defaultEnd = periodOptions.at(0);
  const defaultStart = periodOptions.at(1) ?? periodOptions.at(0);
  const selectedCustomPeriods = getCustomSelectedPeriods(currentCustomPeriods, periodOptions);
  const defaultCustomPeriods =
    selectedCustomPeriods.length > 0
      ? getCustomSelectedPeriods(currentCustomPeriods, periodOptions)
      : [{ from: defaultStart, to: defaultEnd }];
  const [comparisonPeriods, setComparisonPeriods] = useState<ComparisonPeriod[]>([...defaultCustomPeriods]);

  const handleApply = () => {
    const start = comparisonPeriods.map(({ from }) => from);
    const end = comparisonPeriods.map(({ to }) => to);
    onApply(start, end);
  };

  const { date: maxDate } = periodOptions.at(0);
  const { date: minDate } = periodOptions.at(-1);

  const handleOnChange = (type: 'from' | 'to', index: number) => (param: MonthPickerParams | WeekPickerParams) => {
    const value = param.value;
    let period;
    if (typeof value === 'string') {
      period = periodOptions.find(period => period.name === (param as WeekPickerParams).value);
    } else {
      period = periodOptions.find(period => period.name === (param as MonthPickerParams).formatted);
    }
    if (!period) {
      throw new GenericError('Comparision period not found in comparison options');
    }
    const newComparisonPeriod = { ...comparisonPeriods[index], [type]: period };
    const copyComparisionPeriod = [...comparisonPeriods];
    copyComparisionPeriod[index] = newComparisonPeriod;
    setComparisonPeriods(copyComparisionPeriod);
  };

  const areComparisonPeriodsInvalid = (comparisonPeriods: ComparisonPeriod[]) => {
    return comparisonPeriods.reduce((acc, curr) => {
      const current = !curr.from || !curr.to || curr.from.date > curr.to.date;
      return acc || current;
    }, false);
  };
  const isApplyDisabled = areComparisonPeriodsInvalid(comparisonPeriods);

  const addComparisonPeriod = () => {
    const newPeriod: ComparisonPeriod = {
      from: defaultStart,
      to: defaultEnd,
    };
    const newPeriods = [...comparisonPeriods, newPeriod];
    setComparisonPeriods(newPeriods);
  };

  const deleteComparisionPeriod = (index: number) => {
    const filteredPeriod = comparisonPeriods.filter((_, i) => i !== index);
    setComparisonPeriods(filteredPeriod);
  };
  const getCustomPicker = (comparisonPeriod: ComparisonPeriod, index: number) => {
    const periodicity = periodOptions[0].periodicity;
    return periodicity === Periodicity.Weekly ? (
      <CustomWeekPicker
        key={`weekly_${index}`}
        defaultStartDate={comparisonPeriod.from.date}
        defaultEndDate={comparisonPeriod.to.date}
        minDate={minDate}
        maxDate={maxDate}
        onChange={handleOnChange}
        index={index}
      />
    ) : (
      <CustomMonthPicker
        key={`monthly_${index}`}
        defaultStartDate={comparisonPeriod.from.date}
        defaultEndDate={comparisonPeriod.to.date}
        minDate={minDate}
        maxDate={maxDate}
        onChange={handleOnChange}
        index={index}
      />
    );
  };
  //temporary check MAX_ALLOWED_COMPARISON_PERIODS for backward compatibility check. Will removed once bff changes merged to master.
  const customLimitOption =
    parseInt(customMenuModalSettings.customComparisonPeriods?.customLimitOption) || MAX_ALLOWED_COMPARISON_PERIODS;

  const DeleteIcon = Button.Icon.icons['Delete'];
  return (
    <Modal size="md" isOpen={isOpen} onClose={onClose} renderInPortal>
      <Modal.Header>Custom Comparison</Modal.Header>
      <div className="px-6 pt-4 pb-6 flex-grow-0 modal-body overflow-visible">
        {comparisonPeriods.map((period, index, cp) => {
          return (
            <React.Fragment key={`custom_fr_${index}`}>
              <div key={index} className={'flex flex-row justify-between items-end gap-1 overflow-visible pb-4'}>
                {getCustomPicker(period, index)}
                {cp.length > 1 ? (
                  <Button
                    key={`delete_${index}`}
                    mode="simple"
                    testId={`delete-comparison-btn-${index}`}
                    onClick={() => {
                      deleteComparisionPeriod(index);
                    }}
                    label="Delete"
                  >
                    <Button.Icon>
                      <DeleteIcon />
                    </Button.Icon>
                  </Button>
                ) : null}
              </div>

              {customLimitOption > 1 && <MenuSeparatorComponent key={`seperator_${index}`} />}
            </React.Fragment>
          );
        })}
        <div>
          {comparisonPeriods.length < customLimitOption && (
            <Button mode="simple" onClick={addComparisonPeriod} testId="add-comparison-btn">
              Add Comparison period
            </Button>
          )}
        </div>
        {isApplyDisabled && <span className="text-ui-red">{WARNING_MESSAGE_ON_APPLY}</span>}
      </div>
      <Modal.Footer>
        <Button mode="secondary" onClick={onClose} testId="modal-cancel-btn">
          Cancel
        </Button>
        <Button disabled={isApplyDisabled} onClick={handleApply} testId="modal-apply-btn">
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const weekNumberToDate = (weekLabel: string): Date => {
  const [week, year] = weekLabel.split(' ');
  const weekNumber = week.replace('W', '');
  const randomDateInYear = new Date(parseInt(year), 5, 5);
  const startOfFirstWeekDate = startOfWeekYear(randomDateInYear, {
    firstWeekContainsDate: 3 as FirstWeekContainsDate,
  });
  const targetDate = addDays(startOfFirstWeekDate, (parseInt(weekNumber) - 1) * 7);
  return targetDate;
};

const getCustomSelectedPeriods = (
  currentCustomPeriods: string[],
  periodOptions: PeriodWithDate[]
): ComparisonPeriod[] => {
  const selected: ComparisonPeriod[] = [];

  if (currentCustomPeriods && currentCustomPeriods.length > 0) {
    currentCustomPeriods.forEach((val: string) => {
      const [startSeq, endSeq] = val.split('-');
      const start = periodOptions.find(option => option.periodSeq === parseInt(startSeq));
      const end = periodOptions.find(option => option.periodSeq === parseInt(endSeq));
      if (start && end) {
        selected.push({
          from: start,
          to: end,
        });
      }
    });
  }
  return selected;
};
